import React from "react";

/* ─────────────────────────────────────────────────────────────────────
   TabErrorBoundary
   ─────────────────────────────────────────────────────────────────────
   Wraps the content of ONE MDI tab. If that tab's screen throws while
   rendering — a restored form with an unexpected payload, a screen that is
   mid-development, a missing lookup — this catches it and shows a small
   recoverable card for THAT tab only. The rest of AppShell (the tab bar,
   other open tabs, the toolbars) keeps working.

   This is the difference between "one tab shows an error you can close"
   and "the entire application goes to a white screen". Use it in addition
   to RootErrorBoundary, which is the catastrophic-failure backstop one
   level up.

   Usage in AppShell, around each rendered panel:

     <div key={tab.id} style={{ display: tab.id === activeId ? "block" : "none" }}>
       <TabErrorBoundary
         title={tab.title}
         resetKey={tab.id}
         onClose={() => dispatch(closeTab(tab.id))}
       >
         {renderPanelFor(tab)}
       </TabErrorBoundary>
     </div>
   ───────────────────────────────────────────────────────────────────── */
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[TabErrorBoundary]", this.props.title, error, info);
  }

  componentDidUpdate(prevProps) {
    // If the tab identity changes (a new tab reuses this slot), clear the
    // error so the new content gets a fresh chance to render.
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={styles.wrap}>
        <i className="bi bi-exclamation-triangle" style={styles.icon} />
        <div style={styles.title}>This tab couldn&rsquo;t be restored</div>
        <div style={styles.body}>
          It may be left over from a previous version of the app. Close it and
          reopen it from the menu — your saved data on the server is untouched.
        </div>
        <div style={styles.actions}>
          {this.props.onClose && (
            <button style={styles.primary} onClick={this.props.onClose}>
              Close tab
            </button>
          )}
          <button
            style={styles.ghost}
            onClick={() => this.setState({ hasError: false })}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

const styles = {
  wrap: {
    maxWidth: 420,
    margin: "3rem auto",
    textAlign: "center",
    padding: "2rem",
    background: "var(--ds-surface, #fff)",
    border: "1px solid var(--ds-border, #e2e8f0)",
    borderRadius: "var(--ds-radius-lg, 12px)",
    boxShadow: "var(--ds-shadow-sm, 0 2px 8px rgba(0,0,0,.06))",
    fontFamily: "var(--ds-font-family, system-ui, sans-serif)",
  },
  icon: { fontSize: "2rem", color: "var(--ds-warning, #d97706)" },
  title: {
    marginTop: "0.75rem",
    fontWeight: 600,
    color: "var(--ds-text, #0f172a)",
  },
  body: {
    color: "var(--ds-text-muted, #64748b)",
    fontSize: "0.9rem",
    margin: "0.5rem 0 1.25rem",
  },
  actions: { display: "flex", gap: "0.5rem", justifyContent: "center" },
  primary: {
    border: "none",
    borderRadius: "var(--ds-radius-md, 8px)",
    padding: "0.5rem 1.1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#fff",
    background: "var(--ds-primary, #1e40af)",
    cursor: "pointer",
  },
  ghost: {
    border: "1px solid var(--ds-border, #e2e8f0)",
    borderRadius: "var(--ds-radius-md, 8px)",
    padding: "0.5rem 1.1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "var(--ds-text, #0f172a)",
    background: "transparent",
    cursor: "pointer",
  },
};

export default TabErrorBoundary;
